import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface BestCaseTurn {
  turnNumber?: number;
  stageName?: string;
  speaker?: string;
  role?: string;
  dialogue?: string;
  idealLearnerResponse?: string;
  milestonesSatisfied?: string[];
  coachingNote?: string;
}

export interface BestCaseScriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  scenarioId?: string;
  scenarioTitle?: string;
  category?: string;
  passScore?: number;
  timeLimitMinutes?: number;
  learnerRole?: string;
  learnerObjective?: string;
  personaName?: string;
  personaRole?: string;
  openingStatement?: string;
  objectives?: Array<{ id?: string; title: string }> | string[];
  guidelines?: string[];
  scenarioBackground?: string;
  stages?: Array<{ id?: string; title?: string; name?: string; goal?: string; context?: string; order?: number }>;
  scriptTurns?: BestCaseTurn[];
}

export const BestCaseScriptModal: React.FC<BestCaseScriptModalProps> = ({
  isOpen,
  onClose,
  scenarioId,
  scenarioTitle = '',
  category = 'General',
  passScore = 70,
  timeLimitMinutes = 10,
  learnerRole = '',
  learnerObjective = '',
  personaName = '',
  personaRole = '',
  openingStatement = '',
  objectives = [],
  guidelines = [],
  scenarioBackground = '',
  stages = [],
  scriptTurns = [],
}) => {
  const navigate = useNavigate();
  const [regenSeed, setRegenSeed] = useState<number>(0);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  // Validation Checks: Ensure scenario has minimum required information entered
  const hasTitle = Boolean(scenarioTitle && scenarioTitle.trim().length >= 3);
  const hasPersonaName = Boolean(personaName && personaName.trim().length >= 2);
  const hasPersonaRole = Boolean(personaRole && personaRole.trim().length >= 2);
  const hasLearnerRole = Boolean(learnerRole && learnerRole.trim().length >= 2);
  const hasContext = Boolean(
    (openingStatement && openingStatement.trim().length >= 5) ||
    (scenarioBackground && scenarioBackground.trim().length >= 5) ||
    (learnerObjective && learnerObjective.trim().length >= 5) ||
    (objectives && objectives.length > 0)
  );

  const isReadyToGenerate = hasTitle && hasPersonaName && hasPersonaRole && hasLearnerRole && hasContext;

  // Dynamically compute testing suite script matching the actual scenario parameters and persona
  const turns: BestCaseTurn[] = useMemo(() => {
    if (!isReadyToGenerate) {
      return [];
    }

    if (scriptTurns && scriptTurns.length > 0) {
      return scriptTurns;
    }

    const cleanTitle = scenarioTitle.trim();
    const cleanPersonaName = personaName.trim();
    const cleanPersonaRole = personaRole.trim();
    const cleanLearnerRole = learnerRole.trim();
    const firstName = cleanPersonaName.split(' ')[0] || cleanPersonaName;
    const cleanOpening = (openingStatement && openingStatement.trim()) || `Hello, I need assistance regarding ${cleanTitle}.`;

    const rawObjTitles = (objectives || []).map((o: any) => (typeof o === 'string' ? o : o?.title || '')).filter(Boolean);
    const obj1 = rawObjTitles[0] || `Acknowledge ${cleanPersonaName}'s situation and establish key problem context`;
    const obj2 = rawObjTitles[1] || `Investigate root causes and explain options for ${cleanTitle}`;
    const obj3 = rawObjTitles[2] || `Deliver concrete action plan with clear timeline and ownership`;
    const obj4 = rawObjTitles[3] || `Confirm agreed resolution and complete professional closing`;

    const guide1 = (guidelines || [])[0] || 'Acknowledge concerns promptly with empathy and professional ownership.';
    const guide2 = (guidelines || [])[1] || 'Outline structured action steps with realistic commitments.';

    // If custom stages are defined in the scenario, map turns to each stage
    if (stages && stages.length > 0) {
      return stages.map((stg, idx) => {
        const stageName = stg.title || stg.name || `Stage ${idx + 1}`;
        const stageGoal = stg.goal || rawObjTitles[idx] || `Complete ${stageName}`;
        const stageContext = stg.context || (idx === 0 ? cleanOpening : `Addressing ${stageGoal} in ${cleanTitle}`);

        if (idx === 0) {
          return {
            turnNumber: 1,
            stageName: `${stageName}`,
            speaker: cleanPersonaName,
            role: cleanPersonaRole,
            dialogue: cleanOpening,
            idealLearnerResponse: `Hello ${firstName}, thank you for reaching out. As your ${cleanLearnerRole}, I understand the importance of ${cleanTitle.toLowerCase()} and will take ownership to guide this to a complete resolution. Let's look at the key details immediately.`,
            milestonesSatisfied: ['PROBLEM_ESTABLISHED', 'EMPATHY_ACKNOWLEDGED'],
            coachingNote: `Acknowledge ${cleanPersonaName} professionally, confirm your role as ${cleanLearnerRole}, and target: "${stageGoal}".`,
          };
        } else if (idx === stages.length - 1) {
          return {
            turnNumber: idx + 1,
            stageName: `${stageName}`,
            speaker: cleanPersonaName,
            role: cleanPersonaRole,
            dialogue: `Thank you, that gives me clear clarity on next steps and timeline for ${cleanTitle}.`,
            idealLearnerResponse: `You are very welcome, ${firstName}. I have documented all confirmed action items and scheduled our follow-up as agreed. Is there anything else I can clarify for you today?`,
            milestonesSatisfied: ['NEXT_STEP_PROVIDED', 'TIMELINE_PROVIDED', 'PROFESSIONAL_CLOSE'],
            coachingNote: `Reiterate the concrete follow-up timeline and close courteously.`,
          };
        } else {
          return {
            turnNumber: idx + 1,
            stageName: `${stageName}`,
            speaker: cleanPersonaName,
            role: cleanPersonaRole,
            dialogue: stageContext && stageContext !== `Addressing ${stageGoal} in ${cleanTitle}` ? stageContext : `Regarding ${stageGoal.toLowerCase()}: what is our specific action plan and what should I expect?`,
            idealLearnerResponse: `Based on our assessment for ${cleanTitle}, here is the exact roadmap: we will execute ${stageGoal.toLowerCase()} with high precision and keep you updated at every checkpoint.`,
            milestonesSatisfied: [`STAGE_${idx + 1}_OBJECTIVE_MET`, 'OWNERSHIP_MAINTAINED'],
            coachingNote: `Address ${cleanPersonaName}'s inquiries transparently according to: ${guide2}`,
          };
        }
      });
    }

    // Default 4-stage standard synthesis based strictly on scenario data
    return [
      {
        turnNumber: 1,
        stageName: 'Stage 1: Opening & Problem Discovery',
        speaker: cleanPersonaName,
        role: cleanPersonaRole,
        dialogue: cleanOpening,
        idealLearnerResponse: `Hello ${firstName}, thank you for bringing this to my attention. As your ${cleanLearnerRole}, I am here to help you navigate ${cleanTitle.toLowerCase()} and take personal ownership to resolve this. Could you please confirm the key details so we can proceed immediately?`,
        milestonesSatisfied: ['PROBLEM_ESTABLISHED', 'EMPATHY_ACKNOWLEDGED'],
        coachingNote: `Acknowledge ${cleanPersonaName}'s opening concern, state your role as ${cleanLearnerRole}, and establish context. Guideline: ${guide1} | Objective: "${obj1}".`,
      },
      {
        turnNumber: 2,
        stageName: 'Stage 2: Context Analysis & Needs Assessment',
        speaker: cleanPersonaName,
        role: cleanPersonaRole,
        dialogue: scenarioBackground && scenarioBackground.trim().length > 10
          ? `Regarding ${cleanTitle}: ${scenarioBackground.slice(0, 140)}. What are the recommended options and next actions?`
          : `Regarding ${cleanTitle}: I want to understand what specific options or resolutions are available for me right now.`,
        idealLearnerResponse: `Thank you for providing that background, ${firstName}. I have reviewed the requirements for ${cleanTitle.toLowerCase()}. To address ${obj2.toLowerCase()}, here are the exact options and recommendations tailored to your goals.`,
        milestonesSatisfied: ['CONTEXT_ESTABLISHED', 'ROOT_CAUSE_INVESTIGATED'],
        coachingNote: `Explain status and available options with complete domain transparency. Target Objective: "${obj2}".`,
      },
      {
        turnNumber: 3,
        stageName: 'Stage 3: Solution Presentation & Action Plan',
        speaker: cleanPersonaName,
        role: cleanPersonaRole,
        dialogue: `That sounds clear and reasonable. What concrete steps will be taken now and when can I expect this to be completed?`,
        idealLearnerResponse: `I have initiated the confirmed solution directly on your file. You will receive a summary confirmation, and we will execute the agreed deliverables within our committed timeline.`,
        milestonesSatisfied: ['OWNERSHIP_ESTABLISHED', 'SOLUTION_DELIVERED'],
        coachingNote: `Set realistic timelines and execute the action plan. Target: ${guide2} and "${obj3}".`,
      },
      {
        turnNumber: 4,
        stageName: 'Stage 4: Agreement & Next Steps Confirmation',
        speaker: cleanPersonaName,
        role: cleanPersonaRole,
        dialogue: `I appreciate you handling ${cleanTitle} so thoroughly and providing clear answers today. That resolves all my questions.`,
        idealLearnerResponse: `You are very welcome, ${firstName}. I have scheduled our follow-up to ensure everything progresses smoothly. Is there anything else I can assist you with today? Thank you for your partnership.`,
        milestonesSatisfied: ['NEXT_STEP_PROVIDED', 'TIMELINE_PROVIDED', 'PROFESSIONAL_CLOSE'],
        coachingNote: `Confirm next steps and follow-up timeline (${obj4}) and conclude with a courteous farewell.`,
      },
    ];
  }, [
    isReadyToGenerate,
    scenarioTitle,
    category,
    personaName,
    personaRole,
    openingStatement,
    objectives,
    guidelines,
    learnerRole,
    learnerObjective,
    scenarioBackground,
    stages,
    scriptTurns,
    regenSeed,
  ]);

  if (!isOpen) return null;

  const handleRegenerate = () => {
    setIsRegenerating(true);
    setTimeout(() => {
      setRegenSeed((prev) => prev + 1);
      setIsRegenerating(false);
    }, 400);
  };

  const handleDownloadJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify({
      scenarioTitle,
      category,
      passScore,
      timeLimitMinutes,
      learnerRole,
      learnerObjective,
      personaName,
      personaRole,
      scriptTurns: turns,
    }, null, 2));
    const dlAnchor = document.createElement('a');
    dlAnchor.setAttribute('href', dataStr);
    dlAnchor.setAttribute('download', `${(scenarioTitle || 'script').toLowerCase().replace(/[^a-z0-9]+/g, '_')}_best_case.json`);
    document.body.appendChild(dlAnchor);
    dlAnchor.click();
    dlAnchor.remove();
  };

  const handleDownloadText = () => {
    let txt = `=================================================================\n`;
    txt += `TRAINOVA AI — BEST-CASE SIMULATION SCRIPT\n`;
    txt += `Scenario: ${scenarioTitle}\n`;
    txt += `Category: ${category} | Pass Score: ${passScore}% | Time: ${timeLimitMinutes} mins\n`;
    txt += `Target Persona: ${personaName} (${personaRole})\n`;
    txt += `Learner Role: ${learnerRole}\n`;
    txt += `Objective: ${learnerObjective || 'Complete consultative dialogue and satisfy scenario criteria.'}\n`;
    txt += `=================================================================\n\n`;

    turns.forEach((t, i) => {
      txt += `--- TURN ${t.turnNumber || (i + 1)}: ${t.stageName || `Stage ${i + 1}`} ---\n`;
      if (t.milestonesSatisfied?.length) {
        txt += `Milestones: ${t.milestonesSatisfied.join(', ')}\n`;
      }
      txt += `🎭 ${t.speaker || personaName} (${t.role || personaRole}):\n"${t.dialogue}"\n\n`;
      txt += `👤 Ideal Learner Response (${learnerRole}):\n"${t.idealLearnerResponse}"\n\n`;
      if (t.coachingNote) {
        txt += `💡 Coaching Target: ${t.coachingNote}\n\n`;
      }
    });

    const dataStr = 'data:text/plain;charset=utf-8,' + encodeURIComponent(txt);
    const dlAnchor = document.createElement('a');
    dlAnchor.setAttribute('href', dataStr);
    dlAnchor.setAttribute('download', `${(scenarioTitle || 'script').toLowerCase().replace(/[^a-z0-9]+/g, '_')}_script.txt`);
    document.body.appendChild(dlAnchor);
    dlAnchor.click();
    dlAnchor.remove();
  };

  const handleAutoExecute = () => {
    onClose();
    if (scenarioId) {
      navigate(`/simulations/${scenarioId}/briefing`);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(5, 10, 25, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '940px',
          maxHeight: '90vh',
          background: 'var(--white)',
          borderRadius: '16px',
          border: '1px solid var(--line)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--line)',
            background: 'linear-gradient(135deg, #0B192C 0%, #1E3E62 100%)',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '18px' }}>📜</span>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em' }}>
                Best-Case Script {hasTitle ? `: ${scenarioTitle}` : ''}
              </h2>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: '#CBD5E1' }}>
              {isReadyToGenerate ? (
                <>
                  AI Persona: <strong style={{ color: '#FDE047' }}>{personaName}</strong> ({personaRole}) • Target Domain: <strong style={{ color: '#93C5FD' }}>{category}</strong> • Target Score: <strong style={{ color: '#86EFAC' }}>{passScore}%</strong>
                </>
              ) : (
                'Interactive Best-Case Dialogue & Testing Calibration Suite'
              )}
            </p>
          </div>

          {/* Action Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {isReadyToGenerate && (
              <>
                <button
                  type="button"
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '8px',
                    padding: '7px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: isRegenerating ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                  title="Regenerate dialogue turns based on current scenario parameters"
                >
                  <span style={{ display: 'inline-block', transform: isRegenerating ? 'rotate(180deg)' : 'none', transition: 'transform 0.4s ease' }}>🔄</span>
                  {isRegenerating ? 'Calibrating…' : 'Regenerate'}
                </button>

                {scenarioId && (
                  <button
                    type="button"
                    onClick={handleAutoExecute}
                    style={{
                      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                      color: '#FFFFFF',
                      border: 0,
                      borderRadius: '8px',
                      padding: '7px 14px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      boxShadow: '0 2px 8px rgba(37,99,235,0.4)',
                    }}
                  >
                    ⚡ Auto-Execute
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleDownloadJson}
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    padding: '7px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  💾 JSON
                </button>

                <button
                  type="button"
                  onClick={handleDownloadText}
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    padding: '7px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  📄 Text
                </button>
              </>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 0,
                borderRadius: '8px',
                color: '#FFFFFF',
                width: '32px',
                height: '32px',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'grid', gap: '16px', background: 'var(--surface)' }}>
          {/* Validation Empty State if details are missing */}
          {!isReadyToGenerate ? (
            <div
              style={{
                background: 'var(--white)',
                border: '1px solid var(--line)',
                borderRadius: '14px',
                padding: '36px 28px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '28px',
                  background: 'var(--blue-soft, #EFF6FF)',
                  border: '1px solid #BFDBFE',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '26px',
                }}
              >
                📝
              </div>

              <div style={{ maxWidth: '580px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: 'var(--navy)' }}>
                  Scenario Details Required to Generate Best-Case Script
                </h3>
                <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)', lineHeight: 1.6 }}>
                  The Best-Case Simulation Script is generated dynamically from your scenario's parameters.
                  Please complete the essential scenario details in the form to generate the turn-by-turn dialogue and ideal learner responses.
                </p>
              </div>

              {/* Requirement Checklist */}
              <div
                style={{
                  width: '100%',
                  maxWidth: '540px',
                  background: 'var(--surface, #F8FAFC)',
                  border: '1px solid var(--line, #E2E8F0)',
                  borderRadius: '10px',
                  padding: '16px 20px',
                  textAlign: 'left',
                  display: 'grid',
                  gap: '10px',
                  fontSize: '13px',
                }}
              >
                <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '4px', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Required Details Checklist
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--ink)' }}>📌 Scenario Title</span>
                  <span style={{ fontWeight: 600, color: hasTitle ? 'var(--ok)' : 'var(--bad)', fontSize: '12px' }}>
                    {hasTitle ? `✓ "${scenarioTitle.slice(0, 24)}…"` : '✗ Missing'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--ink)' }}>🎭 AI Persona (Name & Role)</span>
                  <span style={{ fontWeight: 600, color: (hasPersonaName && hasPersonaRole) ? 'var(--ok)' : 'var(--bad)', fontSize: '12px' }}>
                    {(hasPersonaName && hasPersonaRole) ? `✓ ${personaName} (${personaRole})` : '✗ Missing'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--ink)' }}>👤 Learner Role</span>
                  <span style={{ fontWeight: 600, color: hasLearnerRole ? 'var(--ok)' : 'var(--bad)', fontSize: '12px' }}>
                    {hasLearnerRole ? `✓ ${learnerRole}` : '✗ Missing'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--ink)' }}>💬 Opening Statement or Context</span>
                  <span style={{ fontWeight: 600, color: hasContext ? 'var(--ok)' : 'var(--bad)', fontSize: '12px' }}>
                    {hasContext ? '✓ Provided' : '✗ Missing'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn primary md"
                  style={{
                    padding: '9px 22px',
                    borderRadius: '8px',
                    fontWeight: 600,
                  }}
                >
                  ← Return to Scenario Form & Fill Details
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Objective Banner */}
              <div
                style={{
                  background: 'var(--white)',
                  border: '1px solid var(--line)',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  display: 'flex',
                  gap: '14px',
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: '20px' }}>📋</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>
                    Scenario Calibration (Learner Role: {learnerRole} • Counterpart: {personaName} [{personaRole}])
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--ink)', lineHeight: 1.5 }}>
                    {learnerObjective || (scenarioBackground ? scenarioBackground.slice(0, 200) : `Conduct a structured consultative conversation regarding ${scenarioTitle}, satisfy configured evaluation milestones, and confirm clear next steps.`)}
                  </p>
                </div>
              </div>

              {/* Turn-by-Turn Timeline */}
              <div style={{ display: 'grid', gap: '14px' }}>
                {turns.map((t, idx) => {
                  const turnNum = t.turnNumber || (idx + 1);
                  return (
                    <div
                      key={idx}
                      style={{
                        background: 'var(--white)',
                        border: '1px solid var(--line)',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                      }}
                    >
                      {/* Turn Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              background: 'var(--blue)',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: '6px',
                              letterSpacing: '0.04em',
                            }}
                          >
                            TURN {turnNum}
                          </span>
                          <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>{t.stageName || `Stage ${turnNum}`}</strong>
                        </div>

                        {/* Milestones */}
                        {t.milestonesSatisfied && t.milestonesSatisfied.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {t.milestonesSatisfied.map((m) => (
                              <span
                                key={m}
                                style={{
                                  background: 'var(--ok-soft)',
                                  color: 'var(--ok)',
                                  fontSize: '10.5px',
                                  fontWeight: 700,
                                  padding: '2px 7px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(16,185,129,0.2)',
                                }}
                              >
                                ✓ {m}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Persona Utterance */}
                      <div
                        style={{
                          background: 'rgba(239, 246, 255, 0.6)',
                          borderLeft: '3px solid var(--blue)',
                          padding: '10px 14px',
                          borderRadius: '0 8px 8px 0',
                          marginBottom: '10px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--navy)', marginBottom: '4px' }}>
                          <span>🎭</span>
                          <span>{t.speaker || personaName} ({t.role || personaRole})</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--ink)', fontStyle: 'italic', lineHeight: 1.5 }}>
                          "{t.dialogue}"
                        </p>
                      </div>

                      {/* Ideal Learner Response */}
                      <div
                        style={{
                          background: 'rgba(240, 253, 244, 0.6)',
                          borderLeft: '3px solid var(--ok)',
                          padding: '10px 14px',
                          borderRadius: '0 8px 8px 0',
                          marginBottom: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--ok)', marginBottom: '4px' }}>
                          <span>👤</span>
                          <span>Ideal Learner Response ({learnerRole}):</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--ink)', lineHeight: 1.5 }}>
                          "{t.idealLearnerResponse}"
                        </p>
                      </div>

                      {/* Coaching Target */}
                      {t.coachingNote && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: 'var(--muted)', marginTop: '6px', paddingLeft: '4px' }}>
                          <span style={{ fontSize: '14px' }}>💡</span>
                          <div>
                            <strong style={{ color: 'var(--navy)' }}>Coaching Target: </strong>
                            {t.coachingNote}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--line)',
            background: 'var(--white)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            Trainova AI Scenario Testing & Calibration Suite
          </span>
          <button
            type="button"
            onClick={onClose}
            className="btn primary md"
            style={{ padding: '8px 24px' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
